/**
 * `GET /v2/admin/me/permissions` (DNKAPI-0670) — what the signed-in operator may do with
 * money, so the console hides what the server would refuse (DNKADM-0375 Phase B).
 *
 * Body `{ canAdjustCredits: boolean, canCancelFullOrNone: true | "pin" | false,
 * canOverrideNoShowFee: true | "pin" | false, canRemoveCards: boolean,
 * canBlockGuests: boolean }`, read from the caller's own token (the last two are additive,
 * DNKAPI-0675: true for OWNER/SUPER_ADMIN/ADMIN, false for STAFF). Optional
 * `locationId` (a v2 location id; 404 if not the tenant's or not active): for STAFF, a
 * location with no manager PIN set answers `false` for the PIN permissions. Without it
 * STAFF always reads `"pin"`. Bearer-only `/v2/admin/**`, tenant server-derived.
 *
 * Parsed defensively: a value outside `true | "pin" | false` makes the whole read `null`, and
 * the caller keeps its fallback rather than guessing. `canRemoveCards` / `canBlockGuests`
 * fail closed on their own: anything but `true` reads `false` (DNKADM-0465), so a server
 * without them still answers the three money fields.
 *
 * `canWaiveNoShowFee` (DNKAPI-0678, additive) is the same tri-state: STAFF reads `"pin"`, or
 * `false` at a location with no manager PIN set. A missing or unknown value reads `"pin"` on
 * its own (DNKADM-0469) — ask for the PIN rather than let a waive through or hide it, and
 * leave the server to decide; a server without the field still answers the other fields.
 *
 * `canComp` (DNKAPI-0686, additive) is the same tri-state for a comp (a free order or
 * membership), answered for EVERY role from the server's governing-PIN lookup: `"pin"` where a
 * manager PIN governs the location (or it can't be read, or no `locationId` was sent); where
 * none does, `true` for OWNER/SUPER_ADMIN/ADMIN and `false` for STAFF (the server refuses that
 * comp). A missing or unknown value reads `null` — an older server — and the comp gate
 * (`compPin.ts`) applies its own fallback; the other fields still parse.
 */
import { request } from './client';

const PATH = '/v2/admin/me/permissions';

export type MoneyPermission = true | 'pin' | false;

export interface MoneyPermissions {
  canAdjustCredits: boolean;
  canCancelFullOrNone: MoneyPermission;
  canOverrideNoShowFee: MoneyPermission;
  /** Waive (`charge:false`) a no-show fee that has a card to charge (DNKAPI-0678). */
  canWaiveNoShowFee: MoneyPermission;
  /**
   * Comp (a free order or membership) — `"pin"` where a manager PIN governs (DNKAPI-0686);
   * `null` when the server did not answer it.
   */
  canComp: MoneyPermission | null;
  /** Remove (and Replace) a saved card (DNKAPI-0675). */
  canRemoveCards: boolean;
  /** Block / unblock a member's guest (DNKAPI-0675). */
  canBlockGuests: boolean;
}

function asPermission(raw: unknown): MoneyPermission | null {
  if (raw === true || raw === false || raw === 'pin') return raw;
  return null;
}

/** The wire body as `MoneyPermissions`, or null when any field is missing or unknown. */
export function parseMoneyPermissions(body: unknown): MoneyPermissions | null {
  if (body === null || typeof body !== 'object') return null;
  const b = body as Record<string, unknown>;
  const cancel = asPermission(b['canCancelFullOrNone']);
  const fee = asPermission(b['canOverrideNoShowFee']);
  if (typeof b['canAdjustCredits'] !== 'boolean' || cancel === null || fee === null) return null;
  return {
    canAdjustCredits: b['canAdjustCredits'],
    canCancelFullOrNone: cancel,
    canOverrideNoShowFee: fee,
    canWaiveNoShowFee: asPermission(b['canWaiveNoShowFee']) ?? 'pin',
    canComp: asPermission(b['canComp']),
    canRemoveCards: b['canRemoveCards'] === true,
    canBlockGuests: b['canBlockGuests'] === true,
  };
}

/** The caller's money permissions, optionally at one location. */
export async function getMyMoneyPermissions(
  locationId?: string | null,
): Promise<MoneyPermissions | null> {
  const body = await request<unknown>(
    'GET',
    PATH,
    locationId ? { query: { locationId } } : {},
  );
  return parseMoneyPermissions(body);
}
